import React, { useEffect, useState } from 'react';
import { Typography,
    Card,
    CardContent,
    Box,
    Alert,
    CircularProgress
} from '@mui/material';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { ShowChartOutlined,
    SentimentNeutral,
    SentimentSatisfiedAltOutlined,
    SentimentVeryDissatisfied,
    CommentOutlined
} from '@mui/icons-material';
import { useParams } from 'react-router-dom'
import PageWithTitleLayout from '../Layout/PageWithTitleLayout';
import { AxiosWithAuth } from '../../Utilities/authenticationService';

const FeedbackManagements = () => {
    const { calendarYearMonth, employmentId } = useParams()
    const [ratingRatios, setRatingRatios] = useState()
    const [ratingCounts, setRatingCounts] = useState()
    const [feedbacks, setFeedbacks] = useState([])
    const [fetchError, setFetchError] = useState(null)
    const [loading, setLoading] = useState(true)
    const year = calendarYearMonth.slice(0, 4)
    const month = calendarYearMonth.slice(5, 7)
    const formattedCalendarYearMonth = `${year}年${month}月`

    const iconMapping = {
        good: <SentimentSatisfiedAltOutlined />,
        neutral: <SentimentNeutral />,
        bad: <SentimentVeryDissatisfied />
    }
    useEffect(() => {
        setLoading(true)
        setFetchError(null)
        AxiosWithAuth.get(`/feedbacks`, {
            params: {
                year_month: calendarYearMonth,
                employment_id: employmentId
            }
        })
            .then(res => {
                const rows = Array.isArray(res.data) ? res.data : []
                let good = 0
                let neutral = 0
                let bad = 0
                rows.forEach(feedback => {
                    if (feedback.rating === 'good') {
                        good++
                    } else if (feedback.rating === 'neutral') {
                        neutral++
                    } else {
                        bad++
                    }
                })
                const total = rows.length
                setRatingCounts({ good, neutral, bad })
                if (total > 0) {
                    setRatingRatios({
                        good: Math.round((good / total) * 100),
                        neutral: Math.round((neutral / total) * 100),
                        bad: Math.round((bad / total) * 100)
                    })
                } else {
                    setRatingRatios({ good: 0, neutral: 0, bad: 0 })
                }
                setFeedbacks(rows)
                setFetchError(null)
            })
            .catch(err => {
                console.log(err)
                setFeedbacks([])
                setRatingCounts({ good: 0, neutral: 0, bad: 0 })
                setRatingRatios({ good: 0, neutral: 0, bad: 0 })
                const status = err.response?.status
                if (status === 403 || status === 401) {
                    setFetchError('この店舗の評価データを表示する権限がありません。')
                } else if (status === 404) {
                    setFetchError('データが見つかりません。')
                } else {
                    setFetchError('評価データを取得できませんでした。')
                }
            })
            .finally(() => setLoading(false))
    }, [calendarYearMonth, employmentId])

    return (
        <PageWithTitleLayout title="総合評価グラフ">
            <Typography sx={{ ml: 5, mt: 5 }}>
                {formattedCalendarYearMonth}
            </Typography>

            {loading && !fetchError && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {fetchError && (
                <Alert severity="error" sx={{ m: 5, maxWidth: '50rem' }}>
                    {fetchError}
                </Alert>
            )}

            {!loading && !fetchError && (
            <>
            <Card sx={{ m: 5, width: '50vw' }}>
                <CardContent>
                    <Typography variant="h7" component="div" sx={{ display: 'flex', alignContent: 'center', fontWeight: 'bold' }}>
                        <ShowChartOutlined sx={{ mr: 1 }} />
                        評価グラフ
                    </Typography>
                    {feedbacks.length ? 
                        <PieChart
                            width={500}
                            height={300}
                            series={[
                                {
                                    data: [
                                        { id: 'good', label: `希望通り ${ratingCounts.good}人`, value: ratingRatios.good, color: '#3FC8E4' },
                                        { id: 'neutral', label: `まあまあ ${ratingCounts.neutral}人`, value: ratingRatios.neutral, color: '#5654D4' },
                                        { id: 'bad', label: `不満 ${ratingCounts.bad}人`, value: ratingRatios.bad, color: '#FB3B52' }
                                    ],
                                    type: 'pie',
                                    innerRadius: 65,
                                    outerRadius: 100,
                                    paddingAngle: 5,
                                    cornerRadius: 5,
                                    startAngle: 0,
                                    endAngle: 360,
                                    cx: 150,
                                    cy: 150,
                                    arcLabel: (d) => `${d.value}%`,
                                    arcLabelsSkipAngle: 35,
                                    arcLabelsRadius: '60%'
                                }
                            ]}
                            sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                    fontWeight: 'bold'
                                },                                
                              }}
                        />
                        :
                        <Typography variant="h7" component="div" sx={{ mt: 3, display: 'flex', alignContent: 'center' }}>
                            今月はまだ評価がありません
                        </Typography>
                    }
                </CardContent>
            </Card>
            <Card sx={{ m: 5 }}>
                <CardContent>
                    <Typography variant="h7" component="div" sx={{ display: 'flex', alignContent: 'center', fontWeight: 'bold' }}>
                        <CommentOutlined sx={{ mr: 1 }} />
                        評価コメント
                    </Typography>
                    {feedbacks.length ? 
                        feedbacks.map((f, index) => (<Box key={index} display='flex' alignItems='center' sx={{ m:2 }}>
                            {iconMapping[f.rating]}
                            <Typography key={index} variant="body1" sx={{ ml: 1 }}>
                                {f.comment}
                            </Typography>
                        </Box>))
                        :
                        <Typography variant="h7" component="div" sx={{ mt: 3, display: 'flex', alignContent: 'center' }}>
                            今月はまだコメントがありません
                        </Typography>
                    }
                </CardContent>
            </Card>
            </>
            )}
        </PageWithTitleLayout>

    )
}

export default FeedbackManagements;